import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output
} from '@angular/core';
import {
  GridColumn,
  GridColumns,
  GRID_COLUMNS_ORDER
} from './grid-column';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { Instrument } from '@iv/model/instrument';
import { getAllInstruments } from '@iv/store/instruments/instrument.reducer';
import { getTime } from '@iv/shared/helpers/common.helper';
import { Destroyer } from 'app/common/utils';
import { SortingDirection } from './preset-header-cell/preset-header-cell.component';

@Component({
  selector: 'iv-live-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
})

export class GridComponent extends Destroyer implements OnInit, OnChanges, AfterViewInit {

  constructor(
    protected _eref: ElementRef,
    @Inject(AppStore) protected store: Redux.Store<AppState>
  ) {
    super();

    const state = this.store.getState();
    this.instruments = getAllInstruments(state);
  }

  get emptyMessage(): string {
    return this._emptyMessage;
  }

  @Input() set emptyMessage(value: string) {
    this._emptyMessage = value;
  }

  @Input() isPreset = false;

  get data(): any[] {
    return this._data;
  }

  @Input() set data(value: any[]) {
    this._data = value;
  }

  get columns(): GridColumns {
    return this._columns;
  }

  @Input() set columns(value: GridColumns) {
    this._columns = value;
    this._data = this.sortData(this._data);
  }

  get scrollBuffer() {
    return this._scrollBuffer;
  }

  @Input() set scrollBuffer(value: number) {
    this._scrollBuffer = value;
    this.maxBufferPx = value + 100;
  }

  get visibleColumns(): string[] {
    return this._visibleColumns;
  }

  @Input() set visibleColumns(value: string[]) {
    this._visibleColumns = value;
    if (Object.keys(this.columns).length !== 0) {
      this.calcAllColsWidth();
      this._data = this.sortData(this._data);
    }
  }

  get instruments(): Instrument[] {
    return this._instruments;
  }

  @Input() set instruments(value: Instrument[]) {
    this._instruments = value;
  }


  @HostBinding('class') class = 'iv-grid';
  @Input() cssClass = '';
  @Input() offScroll: boolean;
  @Input() isNeedCalculators = true;
  @Input() serviceName = '';
  @Output() columnSorting: EventEmitter<string> = new EventEmitter<string>();
  @Output() rowContextMenu = new EventEmitter<any>();
  @Output() allRowsSelection: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() checkboxClick = new EventEmitter<any>();
  @Output() symbolClicked = new EventEmitter<any>();
  @Output() chartButtonClicked = new EventEmitter<any>();
  @Output() calculatorButtonClicked = new EventEmitter<any>();

  public _visibleColumns: string[] = [];
  maxBufferPx = 200;
  allChecked = false;
  header: any[] = [];
  NYtimezone;
  quoteTime;

  private scrollTop = 0;
  private todayDate;
  private _emptyMessage = '';
  private _columns: GridColumns = {};
  private _scrollBuffer = 100;
  private _instruments: Instrument[] = [];
  private parentWidthElementWidth: number;
  private presetSortBySymbolDirection = 'off';

  protected _data: any[] = [];

  private static getRowWidth(grid) {
    let row = <HTMLElement>grid.getElementsByClassName('table-row')[0];
    if (row !== undefined) {
      return row.clientWidth;
    }
    row = null;
    return 0;
  }

  private static getSortField(columns): string[] {
    return columns.map(column => column.dataField);
  }

  private static getDifference(NYTimezone) {
    const timezone = new Date().getTimezoneOffset() / (-60);
    let difference;
    if (timezone >= NYTimezone) {
      difference = Math.abs(NYTimezone) + timezone;
    } else {
      difference = -(NYTimezone - timezone);
    }
    return difference;
  }

  private static getTodayDate(NYTimezone) {
    const difference = GridComponent.getDifference(NYTimezone);
    const todayDate = new Date();
    todayDate.setHours(todayDate.getHours() - difference);
    return todayDate;
  }

  public ngOnInit(): void {
    this.maxBufferPx = this.scrollBuffer;
  }

  ngOnChanges(): void {
    if (!this.isPreset) {
      this._data = this.sortData(this.data);
      if (this.data?.length === 0) {
        this.allChecked = false;
      }
    }
  }

  public ngAfterViewInit(): void {
    this.calcAllColsWidth();
  }

  onTableScroll(e) {
    let tbody = e.target;
    if (this.scrollTop !== tbody.scrollTop) {
      this.scrollTop = tbody.scrollTop;
    } else {
      this.onHorizontalScroll(tbody);
    }
    tbody = null;
  }

  updateColumnsWidth(parentWidthElement?: HTMLDivElement) {
    const grid = this._eref.nativeElement;
    if (parentWidthElement) {
      this.parentWidthElementWidth = parentWidthElement.clientWidth - 20;
    }
    if (grid !== undefined) {
      let parentBoxWidth = this.getBodyWidth(grid);
      if (this.parentWidthElementWidth) {
        parentBoxWidth = parentBoxWidth ? parentBoxWidth : this.parentWidthElementWidth;
      }
      const [columnsWidth, columnsCount] = this.countColumnsWidth();
      const columnWidth = parentBoxWidth / columnsCount;
      for (let i = 0; i < this.visibleColumns.length; i++) {
        const column = this.columns[this.visibleColumns[i]];
        if (column && column.widthCoefficient !== 0) {
          switch (column.dataField) {
            case 'symbol': {
              column.width = 190;
              break;
            }
            case 'strike': {
              column.width = 150;
              break;
            }
            default: {
              column.width = (columnsWidth < parentBoxWidth) ? columnWidth : column.minWidth + 10;
            }
          }

        }
      }
    }
  }

  /**
   * Метод обновляет размер таблицы
   */
  refresh(delay?): void {
    delay = delay ? delay : 0;
    const timeout = setTimeout(() => {
      const grid = this._eref.nativeElement;
      if (grid !== undefined) {
        this.setHeaderWidth(GridComponent.getRowWidth(grid));
      }
      clearTimeout(timeout);
    }, delay);
  }

  getScrollbarWidth() {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.width = '100px';
    // outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    document.body.appendChild(outer);
    const widthNoScroll = outer.offsetWidth;
    // force scrollbars
    outer.style.overflow = 'scroll';
    // add innerdiv
    const inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);
    const widthWithScroll = inner.offsetWidth;
    // remove divs
    outer.parentNode.removeChild(outer);
    return widthNoScroll - widthWithScroll;
  }

  getBodyWidth(grid): number {
    let body = <HTMLElement>grid.getElementsByClassName('table-body-container')[0];
    if (body !== undefined) {
      return body.clientWidth - this.getScrollbarWidth() - this._getConstantColumnsWidth();
    }
    body = null;
    return 0;
  }

  _getConstantColumnsWidth() {
    if (this.visibleColumns.length > 0) {
      let width = 0;
      for (let i = 0; i < this.visibleColumns.length; i++) {
        const column = this.columns[this.visibleColumns[i]];
        if (column && column.widthCoefficient === 0) {
          width += column.minWidth;
        }
      }
      return width;
    } else {
      return 0;
    }
  }

  setHeaderWidth(width) {
    let grid = this._eref.nativeElement;
    if (grid !== undefined) {
      let header = <HTMLElement>grid.getElementsByClassName('table-header')[0];
      if (header !== undefined) {
        header.style.width = width + 'px';
      }
      header = null;
    }
    grid = null;
  }

  countColumnsWidth(): [number, number] {
    if (this.visibleColumns.length > 0) {
      let width = 0;
      let count = 0;
      for (let i = 0; i < this.visibleColumns.length; i++) {
        const column = this.columns[this.visibleColumns[i]];
        if (column && column.widthCoefficient !== 0) {
          width += column.minWidth;
          count++;
        }
      }
      return [width, count];
    } else {
      return [0, 0];
    }
  }

  public sortData(stocksData: any[]): any[] {
    if (!stocksData || (this.isPreset && this.presetSortBySymbolDirection === 'off')) return stocksData;

    const sortedStocksData: any[] = [...stocksData];
    const sortColumns: GridColumn[] = this.getSortColumns();

    if (sortColumns.length > 0) {
      const sortFields: string[] = GridComponent.getSortField(sortColumns);
      if (sortFields.length > 0) {
        this.sortByFields(sortedStocksData, sortFields);
      }
    }

    return sortedStocksData;
  }

  private sortByFields(data: any[], sortFields: string[]) {
    data.sort((a, b) => {
      for (let i = sortFields.length - 1; i >= 0; i--) {
        const sortDirection: number = (this.getSortDirection(sortFields[i]) === 'desc') ? -1 : 1;
        const result: number = this.compareValues(a[sortFields[i]], b[sortFields[i]], sortDirection);
        if (result !== 0) return result;
      }
      return 0;
    });
  }

  private compareValues(first: any, second: any, sortDirection: number): number {
    if (first === undefined || first === null || first === '') return 1;
    if (second === undefined || second === null || second === '') return -1;
    if (first < second) return -sortDirection;
    if (first > second) return sortDirection;
    return 0;
  }

  clickCheckBox(index) {
    event.preventDefault();
    event.stopPropagation();
    this.checkboxClick.emit(this._data[index]);
  }

  emitAllRowsSelectionChange(allChecked: boolean) {
    this.allChecked = allChecked;
    this.allRowsSelection.emit(this.allChecked);
  }

  onTableClick(event: MouseEvent) {
    let target: HTMLElement = event.target as HTMLElement;
    while (target !== null && target.className !== 'table-container') {

      if (target.dataset?.field && target.dataset.field !== 'checkbox') {
        this.columnSorting.emit(target.dataset.field);
        return;
      }
      target = target.parentNode as HTMLElement;
    }
  }

  protected getTableHeader(visibleColumns) {
    let header = [];
    if (visibleColumns.length > 0) {

      for (const column in this.columns) {
        if (visibleColumns.indexOf(column) > -1) {
          header.push(this.columns[column]);
        }
      }
    }
    if (this.serviceName === 'favourites') {
      header = this.sortHeaderColumns(header)
    }
    return header;
  }

  getQuoteTime(row: any) {
    let timeField: string;
    if (row['quotetime']) {
      timeField = 'quotetime';
    } else if (row['atclose_eod']) {
      timeField = 'atclose_eod';
    }

    this.NYtimezone = row[timeField] ? Number(row[timeField].substr(-6, 3)) : -5;
    this.todayDate = GridComponent.getTodayDate(this.NYtimezone);
    let closeDate = getTime(row[timeField]);
    const updateDate = (row.updatedate !== null && row.updatedate !== undefined) ? row.updatedate.substring(0, 10).replace(/-/g, '/').concat(
      ' ',
      getTime(row.updatedate)
    ) : null;
    if (closeDate === '00:00 AM EST') {
      closeDate = '4:00 PM EST';
    }
    closeDate = row[timeField]?.substring(0, 10).replace(/-/g, '/').concat(' ', closeDate);
    const isClose = this.isMarketClose(row.stockid, closeDate, updateDate);
    const isCloseLate = this.isCloseDateLate(closeDate, updateDate);
    return isClose ? closeDate : (this.isCloseDateLate(closeDate, updateDate) ? closeDate : updateDate);
  }

  isCloseDateLate(closeDate, updateDate) {
    if (updateDate == null) {
      return true;
    } else {
      const close = new Date(closeDate);
      const update = new Date(updateDate);
      return close > update;
    }
  }

  isMarketClose(stockId, closeDate, updateDate) {
    return updateDate === null;
    // if (stockId === 18155) {
    //   return true;
    // } else {
    //   return !(!this.isWeekend()
    //     && ((this.todayDate.getTime() - this.getStartMarkterDate().getTime() >= 0) && (this.todayDate.getTime() - this.getEndMarkterDate().getTime() <= 0))
    //   );
    // }
  }

  getStartMarkterDate() {
    return this.getDateWithHours(9, 30);
  }

  getEndMarkterDate() {
    return this.getDateWithHours(16, 20);
  }

  getDateWithHours(hour, minute) {
    const date = new Date();
    date.setHours(hour);
    date.setMinutes(minute);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  isWeekend() {
    const day = new Date().getDay();
    return day === 0 || day === 6;
  }

  trackByFn(index, item) {
    return item.stockid;
  }

  getTrackValue(item) {
    return item.stockid;
  }

  onSymbolButtonClick(symbol) {
    this.symbolClicked.emit(symbol.stockid || symbol.underlyingid || symbol.stockId || symbol.underlyingId);
  }

  onChartButtonClick(symbolName: any, stockId) {
    this.chartButtonClicked.emit({
      stockName: symbolName,
      stockId: stockId
    });
  }

  onCalculatorButtonClick(item) {
    const nameAndExchange = item.symbol + (item.exchange ? (':' + item.exchange) : '');
    Object.assign(item, {nameAndExchange: nameAndExchange, value: 'calc'});
    this.calculatorButtonClicked.emit(item);
  }

  protected calcAllColsWidth(parentWidthElement?: HTMLDivElement): void {
    this.updateColumnsWidth(parentWidthElement);
    this.header = this.getTableHeader(this.visibleColumns);
    this.refresh();
  }

  private onHorizontalScroll(tbody) {
    let grid = this._eref.nativeElement;
    if (grid !== undefined) {
      let header = <HTMLElement>grid.getElementsByClassName('table-header')[0];
      if (header !== undefined) {
        this.setHeaderWidth(GridComponent.getRowWidth(grid));
        header.style.left = (-tbody.scrollLeft) + 'px';
      }
      header = null;
    }
    grid = null;
  }

  private getSortColumns(): GridColumn[] {
    return Object.values(this.columns).filter(item => {
      return (this.visibleColumns.indexOf(item.dataField) > -1)
        && item.sortable === true && (item.sortDirection === SortingDirection.asc || item.sortDirection === SortingDirection.desc);
    });
  }

  /*for favorites*/

  private getSortDirection(column): string {
    return this.columns[column].sortDirection;
  }

  public sortPresetBySymbol(direction: string): void {
    this.presetSortBySymbolDirection = direction;
    this.columns['symbol'].sortDirection = direction;
    this.data = this.sortData(this.data)
  }

  private sortHeaderColumns(header: any): Array<any> {
    const order = GRID_COLUMNS_ORDER;
    return header.sort((a, b) => {
      if (order.indexOf(a.dataField) && order.indexOf(b.dataField)) {
        return order.indexOf(a.dataField) - order.indexOf(b.dataField);
      }
      if (order.indexOf(a.dataField) === -1 && order.indexOf(b.dataField)) {
        return 1;
      }
      if (order.indexOf(a.dataField) && order.indexOf(b.dataField) === -1) {
        return -1;
      }
    });
  }
}
